import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { share, catchError, map, switchMap } from 'rxjs/operators'
import { AcceptLanguageHeader, Language } from '../utility/enums/enums';
import { LoadingScreenService } from '../components/loader/services/loader.service';
import { AppConfig } from 'src/config/app-config.service';
import { Helper } from '../utility/helper/helper';

@Injectable({
    providedIn: 'root'
})
export class HttpService {

    private responseType: any;
    private _authToken: string;
    private _authTimeStamp: Date;

    generateToken$: Observable<void> = of();
    generateTokenComplete: boolean = true;

    generatePublicToken$: Observable<void> = of();
    generatePublicTokenComplete: boolean = true;

    constructor(private _http: HttpClient, private LoadingScreenService: LoadingScreenService) { }

    public post<T>(url: string, body: any, options?: object, checkCurrentUser = false, loader = true): Observable<T> {

        if (loader)
            this.LoadingScreenService.startLoading();

        return this.checkForAuthToken().pipe(switchMap(t => {

            let result$ = new Observable<T>((observer) => {
                let req$ = this._http.post<T>(url, body, this._getOptions(options)).pipe(share(), catchError(this.handleError));

                if (checkCurrentUser && Helper.userInfo().isUserLogged) {

                    if (Helper.userInfo().isAdmin) {
                        location.href = "/" + Language[this.getCurrentLanguage()] + AppConfig.settings.homePageUrl;
                        observer.complete();
                        return;
                    }

                    this.getCurrentUser().subscribe(() => {
                        req$.subscribe(observer);
                    }, (err) => observer.error(err));
                }
                else {
                    req$.subscribe(observer);
                }

            }).pipe(share());

            result$.subscribe(() => this.LoadingScreenService.stopLoading(), () => this.LoadingScreenService.stopLoading());
            return result$;

        }));
    }

    public get<T>(url: string, responseType?: any, options?: object, loader = true): Observable<T> {
        if (loader)
            this.LoadingScreenService.startLoading();

        return this.checkForAuthToken().pipe(switchMap(t => {

            let requestOptions = this._getOptions(options);
            if (responseType) {
                this.responseType = responseType as 'json'
                requestOptions["responseType"] = this.responseType;
            }

            var req$ = this._http.get<T>(url, requestOptions).pipe(share(), catchError(this.handleError));
            req$.subscribe(() => this.LoadingScreenService.stopLoading(), () => this.LoadingScreenService.stopLoading());
            return req$;
        }));

    }

    public delete<T>(url: string, options?: object): Observable<T> {

        this.LoadingScreenService.startLoading();

        return this.checkForAuthToken().pipe(switchMap(t => {

            var req$ = this._http.delete<T>(url, this._getOptions(options)).pipe(share(), catchError(this.handleError));
            req$.subscribe(() => this.LoadingScreenService.stopLoading(), () => this.LoadingScreenService.stopLoading());
            return req$;

        }));
    }

    public put<T>(url: string, body: any, options?: object, checkCurrentUser = false, loader = true): Observable<T> {

        if (loader)
            this.LoadingScreenService.startLoading();

        return this.checkForAuthToken().pipe(switchMap(t => {

            let result$ = new Observable<T>((observer) => {
                let req$ = this._http.put<T>(url, body, this._getOptions(options)).pipe(share(), catchError(this.handleError));
                if (checkCurrentUser && Helper.userInfo().isUserLogged) {

                    if (Helper.userInfo().isAdmin) {
                        location.href = "/" + Language[this.getCurrentLanguage()] + AppConfig.settings.homePageUrl;
                        observer.complete();
                        return;
                    }

                    this.getCurrentUser().subscribe(() => {
                        req$.subscribe(observer);
                    }, (err) => observer.error(err));
                }
                else {
                    req$.subscribe(observer);
                }
            }).pipe(share());

            result$.subscribe(() => this.LoadingScreenService.stopLoading(), () => this.LoadingScreenService.stopLoading());
            return result$;

        }));
    }


    private getAcceptHeader(): string {
        return AcceptLanguageHeader[this.getCurrentLanguage()];
    }

    private getCurrentLanguage(): number {
        return _spPageContextInfo.currentLanguage;
    }

    private handleError = (error: any): Observable<any> => {
        if (error.status == 401) {

            if (!Helper.userInfo().isAdmin) {
                location.href = "/" + Language[this.getCurrentLanguage()] + AppConfig.settings.signUrl + "?ReturnUrl=" + document.location.pathname + document.location.search + document.location.hash;
                return;
            }
        }
        return throwError(error)
    };

    private getCurrentUser() {
        return this.get<any>(AppConfig.settings.authenticationApi + '/users/current/');
    }

    private _getOptions(options: any) {

        if (options == null) {
            options = {};
        }

        if (options.headers == null) {
            options.headers = {};
        }

        options.headers["Accept-Language"] = options.headers["Accept-Language"] || this.getAcceptHeader();

        if (this._authToken) {
            options.headers["Authorization"] = "Bearer " + this._authToken;
        }

        return options;
    }

    public checkForAuthToken(): Observable<any> {

         let userInfo = Helper.userInfo();

        //If Anonymous users, Go Away!
        if (!userInfo || !userInfo.isUserLogged)
            return of({});

        //To reset this.authToken
        this.extractAuthToken();

        //No cookie found
        if (!this._authToken) {

            if (userInfo.isAdmin) {
                //Look for new Token
                return this.generateNewAdminAuthToken();
            }
            else {
                return this.generateNewPublicAuthToken();
            }

        }
        else {

            let authTimeAfterExpiry = new Date(this._authTimeStamp.getTime());
            authTimeAfterExpiry.setSeconds(authTimeAfterExpiry.getSeconds() + AppConfig.settings.adminTokenExpiryInSecond);

            //Expired Token
            if (authTimeAfterExpiry < new Date()) {
                if (userInfo.isAdmin) {
                    return this.generateNewAdminAuthToken();

                }
                else {
                    //Look for new Token
                    return this.generateNewPublicAuthToken();
                }
            }
        }

        return of({});
    }

    private extractAuthToken(): boolean {

        var cookieStringValue = Helper.getCookieValue("access_token");

        if (cookieStringValue) {

            let value = JSON.parse(cookieStringValue);
            this._authToken = value.token;
            this._authTimeStamp = new Date(value.stamp);

            return true;
        }
        else {

            this._authToken = null;
            this._authTimeStamp = null;
            return false;
        }

    }

    private generateNewAdminAuthToken(): Observable<any> {

        //Share the same observable in case of multiple requests
        if (this.generateTokenComplete) {

            this.generateTokenComplete = false;

            this.generateToken$ = this._http.get(AppConfig.settings.authenticationApi + AppConfig.settings.ApiSegments.generateToken, this._getOptions(null))
                .pipe(map(res => { this.extractAuthToken(); })).pipe(share());

            this.generateToken$.subscribe(x => { }, x => { location.reload(); }, () => this.generateTokenComplete = true);
        }

        return this.generateToken$;
    }

    private generateNewPublicAuthToken(): Observable<any> {

        //Share the same observable in case of multiple requests
        if (this.generatePublicTokenComplete) {

            this.generatePublicTokenComplete = false;

            this.generatePublicToken$ = this._http.get(AppConfig.settings.authenticationApi + "/users/p/token", this._getOptions(null))
                .pipe(map(res => { this.extractAuthToken(); })).pipe(share());

            this.generatePublicToken$.subscribe(x => { }, x => { location.reload(); }, () => this.generatePublicTokenComplete = true);
        }

        return this.generatePublicToken$;
    }
}